// Pure helpers shared by every Voter Management page (Voter Search, Age Wise
// Search, Alphabetical Search, Add Voter, Delete Voter, Upload). Extracted
// verbatim from the old monolithic FullVoter.js during the sidebar
// restructure - logic unchanged, just relocated so it isn't duplicated per
// page.

export const DEFAULT_PAGE_SIZE = 25;
export const BOOTH_PAGE_SIZE = 5000;
// Full-list loading (useVoterListFetch.getFullVoterList): the backend
// returns at most SERVER_MAX_PAGE_SIZE voters per request, so the list is
// pulled in blocks of that size, FETCH_CONCURRENCY at a time.
export const SERVER_MAX_PAGE_SIZE = 5000;
export const FETCH_CONCURRENCY = 4;
export const FETCH_RETRIES = 2;
// Search inputs wait this long after the last keystroke before filtering.
export const FILTER_DEBOUNCE_MS = 300;
// Wait before starting a list download, so quick assembly/booth changes
// (e.g. the first booth being auto-selected on load) settle first.
export const LIST_LOAD_DEBOUNCE_MS = 500;
export const PER_PAGE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

export const getOptionValue = (value) =>
  value && typeof value === "object" ? value.value ?? value.label : value;

export const createOption = (value) =>
  value !== null && value !== undefined && value !== ""
    ? { label: String(value), value }
    : null;

export const findOption = (options, value) => {
  const normalizedValue = String(getOptionValue(value) ?? "").toLowerCase();
  if (!normalizedValue) return null;
  return (
    options?.find(
      (item) =>
        String(item.value ?? "").toLowerCase() === normalizedValue ||
        String(item.label ?? "").toLowerCase() === normalizedValue
    ) ?? createOption(value)
  );
};

export const uniqueOptions = (values) =>
  [
    ...new Set(
      values
        .map((value) => getOptionValue(value))
        .filter((value) => value !== null && value !== undefined && value !== "")
    ),
  ].map((value) => ({ label: String(value), value }));

export const colourLabel = (value) =>
  ({ 1: "Green", 2: "Red", 3: "Yellow", 4: "Blue" }[getOptionValue(value)] ||
    value ||
    "-");

export const votedLabel = (value) =>
  value === true || value === 1 || value === "1" ? "Voted" : "Non Voted";

// Visual class for the table's Color swatch - mirrors colourLabel above
// (1=Green/2=Red/3=Yellow/4=Blue). "Gray" covers a genuinely unassigned
// colour - the API sends -1 for this (as well as null/undefined in older
// records), neither of which matches any key below so both fall through to
// the "Gray" default correctly. See voterStats (useVoterListFetch.js) for
// how these map to the Supporter/Neutral/Opposition/Not Assigned stat cards.
export const getColourClassName = (colour) =>
  ({ 1: "Green", 2: "Red", 3: "Yellow", 4: "Blue" }[getOptionValue(colour)] ||
    "Gray");

const normalizeFieldName = (name) =>
  String(name).toLowerCase().replace(/[^a-z0-9]/g, "");

export const getVoterField = (voter, aliases) => {
  const sources = [
    voter,
    voter?.fields,
    voter?.voter,
    voter?.voter_details,
    voter?.details,
  ].filter(Boolean);
  const normalizedAliases = aliases.map(normalizeFieldName);

  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (
        normalizedAliases.includes(normalizeFieldName(key)) &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return value;
      }
    }
  }

  return "";
};

export const normalizeVoter = (voter) => ({
  ...voter,
  age: getVoterField(voter, [
    "age",
    "voter_age",
    "age_years",
    "age_year",
    "voter_age_years",
  ]),
  voter_sr_number: getVoterField(voter, [
    "voter_sr_number",
    "voter_sr_no",
    "voter_serial_number",
    "voter_serial_no",
    "vsrn",
    "vsr_no",
    "vs_no",
    "part_serial_number",
    "serial_number",
    "sr_no",
  ]),
  section_number: getVoterField(voter, [
    "section_number",
    "section_no",
    "section",
    "section_id",
  ]),
  caste: getVoterField(voter, ["caste", "cast", "caste_english", "community"]),
});

// Booth dropdown options from GET voter/booth-number?assembly_numbers=N.
// Accepts the booth list wherever the response puts it (results.booth_numbers,
// booth_numbers, results, data, ...) and items that are plain numbers/strings
// or objects ({ booth_number, booth_no, ... }), so a response-shape
// difference doesn't silently leave the dropdown empty. Sorted numerically.
export const parseBoothOptions = (res) => {
  const candidates = [
    res?.results?.booth_numbers,
    res?.booth_numbers,
    res?.results?.data,
    res?.results?.results,
    res?.results,
    res?.data?.booth_numbers,
    res?.data,
    res,
  ];
  const list = candidates.find((value) => Array.isArray(value)) || [];
  const boothValue = (item) =>
    item && typeof item === "object"
      ? item.booth_number ?? item.booth_no ?? item.booth ?? item.number ?? item.value ?? item.id
      : item;
  const values = [
    ...new Set(list.map(boothValue).filter((value) => value !== undefined && value !== null && value !== "")),
  ];
  return values
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
    .map((value) => ({ label: String(value), value }));
};

const lower = (value) => String(value ?? "").toLowerCase();
const isSet = (value) => value !== undefined && value !== null && value !== "";

// Client-side version of the voter list filters, run over the downloaded
// list (see useVoterListFetch). Text fields match case-insensitively
// anywhere in the value (English or Hindi); dropdowns match exactly.
export const filterVoters = (voters, filter = {}) => {
  const checks = [];
  if (isSet(filter.booth_number)) {
    const booth = String(filter.booth_number);
    checks.push((v) => String(v.booth_no ?? v.booth_number ?? "") === booth);
  }
  if (isSet(filter.epic_number)) {
    const q = lower(filter.epic_number).trim();
    checks.push((v) => lower(v.epic_no).includes(q));
  }
  if (isSet(filter.first_name_e)) {
    const q = lower(filter.first_name_e).trim();
    checks.push((v) => lower(v.first_name_e).includes(q) || lower(v.first_name_h).includes(q));
  }
  if (isSet(filter.last_name_e)) {
    const q = lower(filter.last_name_e).trim();
    checks.push((v) => lower(v.last_name_e).includes(q) || lower(v.last_name_h).includes(q));
  }
  if (isSet(filter.mobile_no)) {
    const q = String(filter.mobile_no).replace(/\D/g, "");
    if (q) checks.push((v) => String(v.mobile_no ?? "").includes(q));
  }
  if (isSet(filter.voter_type)) {
    checks.push((v) => String(getOptionValue(v.voter_type)) === String(filter.voter_type));
  }
  if (isSet(filter.colour)) {
    checks.push((v) => String(getOptionValue(v.colour)) === String(filter.colour));
  }
  if (isSet(filter.voted)) {
    const wantVoted = Number(filter.voted) === 1;
    checks.push((v) => (v.is_voted === true) === wantVoted);
  }
  if (isSet(filter.cast)) {
    const q = lower(filter.cast);
    checks.push((v) => [v.caste, v.cast, v.caste_english].some((c) => lower(getOptionValue(c)) === q));
  }
  if (isSet(filter.section_number)) {
    checks.push((v) => String(v.section_number) === String(filter.section_number));
  }
  if (filter.hasMobileOnly) checks.push((v) => !!v.mobile_no);

  if (!checks.length) return voters;
  return voters.filter((voter) => checks.every((check) => check(voter)));
};

export const maskMobileNumber = (mobileNumber) => {
  const value = String(mobileNumber ?? "");

  if (!value) return "-";
  if (value.length <= 4) return "X".repeat(value.length);

  return `${value.slice(0, 4)}${"X".repeat(
    Math.max(value.length - 6, 2)
  )}${value.slice(-2)}`;
};

// Splits a long house-number string across two lines for the table's
// expand/collapse H.No cell.
export const splitHno = (value) => {
  if (!value) return { line1: "-", line2: null };
  const str = String(value);
  if (str.length <= 12) return { line1: str, line2: null };
  const mid = Math.floor(str.length / 2);
  let splitAt = str.lastIndexOf(" ", mid);
  if (splitAt === -1) splitAt = str.lastIndexOf("-", mid);
  if (splitAt === -1) splitAt = mid;
  return {
    line1: str.slice(0, splitAt).trim(),
    line2: str.slice(splitAt).trim(),
  };
};

export const colorLIst = [
  { label: "Green", value: 1 },
  { label: "Red", value: 2 },
  { label: "Yellow", value: 3 },
  { label: "Blue", value: 4 },
];

export const votedLIst = [
  { label: "Non Voted", value: 0 },
  { label: "Voted", value: 1 },
];

export const voterTypeList = [
  { label: "Voter", value: 2 },
  { label: "Other", value: 1 },
];
